import { PlanSeq } from '@mobi/api-types'
import { getPlanSequenceFromSelection } from '@mobi/betslip/helpers/getPlanSequence'
import { isFobDetails } from '@mobi/betslip/helpers/typeGuards'
import dayjs from '@mobi/utils/date/extendedDayjs'
import { getTicketLayoutType } from '@core/Areas/Account/Pages/Activity/Components/Ticket/helpers'
import type { Ticket } from '@core/Areas/Account/Pages/Activity/types'
import type { BetLoopShareTicket } from '@core/Areas/BetLoop/types'
import type { ShareBetData } from '@core/Areas/ShareBet/types'
import { RacingEvents } from '@core/Areas/VenueMode/Components/Racing/Data/types'
import { isVenueModeTicket } from '@core/Areas/VenueMode/Utils/isVenueModeTicket'

const allowedPlanSeqShares = [
  PlanSeq.Unknown,
  PlanSeq.Win,
  PlanSeq.Place,
  PlanSeq.WinAndPlace,
  PlanSeq.Exacta,
  PlanSeq.ExactaWithRovingBanker,
  PlanSeq.Quinella,
  PlanSeq.Trifecta,
  PlanSeq.TrifectaWithRovingBanker,
  PlanSeq.First4,
  PlanSeq.First4WithRovingBanker,
  PlanSeq.Double,
  PlanSeq.Quaddie,
  PlanSeq.FOBRacing,
]

export const shouldAllowShareInBetLoop = (data: ShareBetData, venueModeEvents?: RacingEvents) => {
  if (data.contextType === 'ticket' && isVenueModeTicket(data.share.ticket, venueModeEvents)) {
    return false
  }

  if (data.contextType === 'kambi') {
    if (data.share.coupon.systemBets && data.share.coupon.systemBets.length > 0) return false
    return true
  }

  if (data.contextType === 'ticket') {
    const leg = data.share.ticket.legs[0]
    const isComboMulti = data.share.ticket.formula !== null

    return !(
      data.share.ticket.productName.startsWith('Insurance') ||
      leg.eventType === 'Greys Box Challenge' ||
      leg.eventType === 'Jockey Challenge' ||
      !allowedPlanSeqShares.includes(data.share.ticket.planSeq) ||
      isComboMulti ||
      hasEventsStarted(data.share.ticket)
    )
  }

  if (data.contextType === 'betslip' || data.contextType === 'quickbet') {
    const details = data.share.items[0].selectionDetails
    const selection = data.share.items[0].selection
    const isFoo = isFobDetails(details)
    const isComboMulti = Object.values(data.share.multiReceipt?.formulaResponse ?? {}).some(
      v => v !== null
    )

    return !(
      ('marketName' in selection && selection.marketName?.startsWith('Insurance')) ||
      ('marketCode' in selection && selection.marketCode === 'CON') ||
      (isFoo && details.competitionName === 'Greys Box Challenge') ||
      (isFoo && details.competitionName === 'Jockey Challenge') ||
      !allowedPlanSeqShares.includes(getPlanSequenceFromSelection(data.share.items[0].selection)) ||
      isComboMulti ||
      data.betSource === 'venue-mode'
    )
  }

  return false
}

// =============
// Local Helpers
// =============

function hasEventsStarted(ticket: Ticket | BetLoopShareTicket): boolean {
  const currentTime = dayjs()
  const ticketLayoutType = getTicketLayoutType(ticket)

  if (ticketLayoutType === 'multi') {
    return ticket.legs.some(leg => currentTime.isAfter(leg.advertisedStartTime))
  }

  if (ticketLayoutType === 'single-multiple' && ticket.allUpStartTimes) {
    return ticket.allUpStartTimes.some(time => currentTime.isAfter(time))
  }

  return currentTime.isAfter(ticket.legs[0].advertisedStartTime)
}
